/**
 * Canonical GamePopup: embeds game-launch.js via launchGame() into a host div.
 */
import { useCallback, useEffect, useRef } from 'react';
import { trackGameReady } from '../../helpers/analytics';
import { launchGame } from '../../helpers/gameLauncher';
import type { GamePopupProps } from './types';
import styles from './GamePopup.module.css';

declare global {
    interface Window {
        AtemoyaLauncher?: { closeGame: () => void | Promise<void> };
    }
}

type CanonicalGamePopupProps = Omit<GamePopupProps, 'format'>;

export function CanonicalGamePopup({ url, name, onClose }: CanonicalGamePopupProps) {
    const rootElementRef = useRef<HTMLDivElement>(null);
    const cleanupRef = useRef<(() => void | Promise<void>) | null>(null);
    const pendingCleanupRef = useRef<Promise<void>>(Promise.resolve());
    const closingRef = useRef(false);

    const destroyThenClose = useCallback(async () => {
        if (closingRef.current) {
            return;
        }
        closingRef.current = true;

        const cleanup = cleanupRef.current;
        cleanupRef.current = null;

        try {
            if (cleanup) {
                await cleanup();
            }
        } finally {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        window.AtemoyaLauncher = {
            closeGame: () => destroyThenClose(),
        };
        return () => {
            if (window.AtemoyaLauncher?.closeGame) {
                delete window.AtemoyaLauncher;
            }
        };
    }, [destroyThenClose]);

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            const data = event.data;
            if (!data || data.type !== 'atemoya-close') {
                return;
            }
            void destroyThenClose();
        }

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [destroyThenClose]);

    useEffect(() => {
        const rootElement = rootElementRef.current;
        if (!rootElement) {
            return;
        }

        let cancelled = false;
        closingRef.current = false;

        void (async () => {
            await pendingCleanupRef.current;
            if (cancelled) {
                return;
            }

            try {
                const cleanup = await launchGame(rootElement, url, {
                    onGameReady: () => trackGameReady(name),
                    isStale: () => cancelled,
                });
                if (cancelled) {
                    await cleanup();
                    return;
                }
                cleanupRef.current = cleanup;
            } catch (error) {
                console.error('[GamePopup] Failed to launch game:', error);
            }
        })();

        return () => {
            cancelled = true;
            const cleanup = cleanupRef.current;
            cleanupRef.current = null;
            pendingCleanupRef.current = (async () => {
                // destroyThenClose already awaited cleanup; skip double destroy.
                if (cleanup) {
                    await cleanup();
                }
            })();
        };
    }, [url, name]);

    return (
        <div className={`${styles.popup} ${styles.active}`}>
            <div className={styles.overlay} />
            <div className={styles.content}>
                <div className={styles.body}>
                    <div ref={rootElementRef} className={styles.iframe} />
                </div>
            </div>
        </div>
    );
}
